import React, { useEffect, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Route, RouterProvider, createBrowserRouter, createRoutesFromElements, useParams } from 'react-router-dom'

// import Feedback from 'simple-screenshot-feedback'
import { SocketProvider } from './context/socket'
import Layout from './pages/layout' // https://reactrouter.com/en/main/route/route#layout-routes
import Login from './pages/login'
import Signup from './pages/signup'
import Settings from './pages/settings'
import Team from './pages/team'
import SupervisorMeetings from './pages/supervisor'
import VerfiyUser from 'src/pages/VerifyUser'
import Pricing from './pages/pricing'
import { WorkspaceProvider } from 'src/context/workspace'
import ResetPassword from 'src/pages/reset'
import Invitation from 'src/pages/invitation'
import { MilkdownProvider } from '@milkdown/react'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { googleSiteKey } from './lib/env'
import UrsPageSupervisor from 'src/pages/supervisor/urds'
import Mixpanel from './lib/mixpanel'
import { SubscriptionProvider } from 'src/context/subscription'
import ClientPage from './pages/client'
import Audit from 'src/pages/audit'
import Error404 from 'src/pages/404'
import SettingsLanding from 'src/pages/settings/landing'
import { Crisp } from 'crisp-sdk-web'
import CallbackAuth from 'src/pages/login/CallbackAuth'
import PowerDocsList from 'src/pages/powerdocs'
import PowerDocument from 'src/pages/powerdoc-view'
import PowerDocProjectView from '@/pages/powerdoc-project-view'
import PowerDocProjectOverView from '@/pages/powerdoc-project-overview'
import PublicTenders from './pages/tenders'
import TenderView from './pages/tender-view'
import TenderMatchingForm from './pages/tender-matching-form'
import TenderFinderShared from './pages/tender-finder-shared'
import OnboardingFormWizard from './pages/admin/pages/onboarding'
import Pipelines from './pages/admin/pages/pipelines'
import AdminTenderView from './pages/admin/pages/tenders'
import TendersReport from './pages/reports'
import CompanyProfile from './pages/settings/company-profile'
import Subscribe from './pages/subscribe'
import Home from './pages/home'

const SettingsWithTab = () => {
  const { tab } = useParams()
  return <Settings tab={tab} />
}

export default function App() {
  const queryClient = useMemo(() => new QueryClient({}), [])

  useEffect(() => {
    const userString = localStorage.getItem('user')
    if (userString) {
      try {
        const user = JSON.parse(userString)
        Mixpanel.login(user.uuid)
      } catch (err) {}
    }
  }, [])

  useEffect(() => {
    Crisp.configure('db4acea6-4989-453d-b963-92ed4fe0a0ef')
  }, []) // Empty dependency array ensures it runs only once after mounting

  return (
    <GoogleReCaptchaProvider reCaptchaKey={googleSiteKey}>
      <MilkdownProvider>
        <WorkspaceProvider>
          <SocketProvider>
            <QueryClientProvider client={queryClient}>
              <SubscriptionProvider>
                <RouterProvider
                  router={createBrowserRouter(
                    createRoutesFromElements(
                      <>
                        <Route path="/tender-finder" element={<TenderMatchingForm />} />
                        <Route path="/tenders-finder/:formId" element={<TenderFinderShared />} />
                        <Route element={<Layout />}>
                          <Route path="/" element={<Home />} />
                          <Route path="/create" element={<Home />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/auth/:social/callback" element={<CallbackAuth />} />
                          <Route path="/signup" element={<Signup />} />
                          <Route path="/invitation/:invitationUUID" element={<Invitation />} />
                          <Route path="/account/reset" element={<ResetPassword />} />
                          <Route path="/account/reset/:token" element={<ResetPassword />} />
                          <Route path="/verify/user/:token" element={<VerfiyUser />} />
                          <Route path="/verify/user/" element={<VerfiyUser />} />
                        <Route
                          path="/document/:documentUUID"
                          element={<PowerDocument variant={'proposal'} isReadOnly={true} />}
                        />
                        <Route
                          path="/proposal/:documentUUID"
                          element={<PowerDocument variant={'proposal'} isReadOnly={true} />}
                        />
                        <Route
                          path="/offer/:documentUUID"
                          element={<PowerDocument variant={'offer'} isReadOnly={true} />}
                        />
                        <Route
                          path="/quotation/:documentUUID"
                          element={<PowerDocument variant={'quotation'} isReadOnly={true} />}
                        />
                        <Route path="/supervisor/urs/:ursUUID" element={<UrsPageSupervisor />} />
                        <Route path="/settings" element={<SettingsLanding />} />
                        <Route path="/settings/company-profile" element={<CompanyProfile />} />
                        <Route path="/settings/:tab" element={<SettingsWithTab />} />
                        <Route path="/team" element={<Team />} />
                        <Route path="/audit" element={<Audit />} />
                        <Route path="/pipeline" element={<PowerDocsList />} />
                        <Route path="/client/:clientUUID" element={<ClientPage />} />
                        <Route path="/client/:clientUUID/project/:documentUUID" element={<PowerDocProjectOverView />} />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/documents"
                          element={<PowerDocProjectOverView tabSlug="documents" />}
                        />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/overview"
                          element={<PowerDocProjectOverView tabSlug="overview" />}
                        />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/attachments"
                          element={<PowerDocProjectOverView tabSlug="attachments" />}
                        />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/custom/:customDocUUID"
                          element={<PowerDocProjectView variant="CUSTOM" />}
                        />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/proposal"
                          element={<PowerDocProjectView variant="PROPOSAL" />}
                        />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/quotation"
                          element={<PowerDocProjectView variant="QUOTATION" />}
                        />
                        <Route
                          path="/client/:clientUUID/project/:documentUUID/offer"
                          element={<PowerDocProjectView variant="OFFER" />}
                        />
                        <Route
                          path="/client/:clientUUID/document/:documentUUID/proposal"
                          element={<PowerDocument variant={'proposal'} />}
                        />
                        <Route
                          path="/client/:clientUUID/document/:documentUUID/quotation"
                          element={<PowerDocument variant={'quotation'} />}
                        />
                        <Route
                          path="/client/:clientUUID/document/:documentUUID/offer"
                          element={<PowerDocument variant={'offer'} />}
                        />
                        <Route
                          path="/client/:clientUUID/document/:documentUUID/custom/:customDocUUID"
                          element={<PowerDocument variant={'custom'} />}
                        />
                        <Route path="/client/:clientUUID/project/:projectUUID" element={<PowerDocsList />} />
                        <Route
                          path="/supervisor"
                          element={<SupervisorMeetings />}
                          handle={{
                            crumb: () => ({ href: `/supervisor`, name: 'Supervisor View', key: 'supervisor' }),
                          }}
                        />
                        <Route path="/pricing" element={<Pricing />} />
                        {/*<Route path="/tenders" element={<PublicTenders />} />*/}
                        <Route path="/admin/tenders" element={<PublicTenders isPrivate={false} isAdmin={true} />} />
                        <Route
                          path="/admin/tender/:tenderUUID/overview"
                          element={<TenderView tabSlug="overview" isAdmin={true} />}
                        />
                        <Route
                          path="/admin/tender/:tenderUUID/documents"
                          element={<TenderView tabSlug="documents" isAdmin={true} />}
                        />
                        <Route path="/tenders" element={<PublicTenders isPrivate={false} isAdmin={false} />} />

                        <Route path="/my-tenders" element={<PublicTenders isPrivate={true} isAdmin={false} />} />
                        <Route path="/my-tenders/:tenderUUID/overview" element={<TenderView tabSlug="overview" />} />
                        <Route path="/my-tenders/:tenderUUID/documents" element={<TenderView tabSlug="documents" />} />
                        <Route path="/admin/onboard" element={<OnboardingFormWizard />} />
                        <Route path="/admin/pipelines" element={<Pipelines />} />
                        <Route path="/admin/pipeline/:tenderUUID" element={<AdminTenderView />} />
                        <Route path="/reports/:userUUID" element={<TendersReport />} />
                        <Route path="/subscribe" element={<Subscribe />} />
                        <Route path="*" element={<Error404 />} />
                        </Route>
                      </>,
                    ),
                  )}
                />
                {/* <Feedback
                  slackToken="xoxb-397530728805-6237601335397-xBkXPySkrvwRzDcFEQzGCMQO"
                  slackChannel="#feedback"
                  handleSubmitError={(errormsg: any) => console.log(errormsg)}
                  location="top-right"
                /> */}
                <ReactQueryDevtools />
              </SubscriptionProvider>
            </QueryClientProvider>
          </SocketProvider>
        </WorkspaceProvider>
      </MilkdownProvider>
    </GoogleReCaptchaProvider>
  )
}
